<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('report.reports') }}</span>
        <h3 class="page-title">{{ $t('report.reportsGenerating') }}</h3>
      </d-col>
    </d-row>

    <d-card style="width: 100%;" class="p-4">
      <h5>{{ $t('report.dailyReport') }}</h5>

      <d-row>
        <d-col cols="12" md="4" lg="4">
          <label>
            {{ $t('report.dateRange') }}:
          </label>

          <date-range-picker
            ref="daily-report-date-picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="w-100"
            :singleDatePicker="true"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="dailyReport.date_range"
            :ranges="false"
            :linkedCalendars="false">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }}
            </template>
          </date-range-picker>
        </d-col>

        <d-col cols="12" md="3" lg="3">
          <label>
            {{ $t('users.customer') }}:
          </label>

          <select-customer
            v-model="dailyReport.customer_id"
            :customerID="dailyReport.customer_id" />
        </d-col>

        <d-col cols="12" md="3" lg="3">
          <label>
            {{ $t('users.worker') }}:
          </label>

          <select-user
            v-model="dailyReport.user_id"
            :userID="dailyReport.user_id" />
        </d-col>

        <d-col cols="12" md="2" lg="2">
          <label class="w-100"></label>
          <d-button
            theme="primary"
            class="w-100 mt-2"
            @click.prevent="downloadDailyReport">
            <template v-if="!downloadingDailyReport">
              {{ $t('report.generateReport') }}
            </template>
            <template v-else>
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </template>
          </d-button>
        </d-col>
      </d-row>
    </d-card>

    <d-card
      v-if="allDataLoaded"
      style="width: 100%;" class="p-4 mt-3">
      <h5>{{ $t('report.purchasesReports') }}</h5>

      <d-row>
        <d-col cols="12" md="2" lg="2">
          <label>
            {{ $t('report.reportType') }}:
          </label>

          <d-form-select
            v-model="purchaseReport.type"
            :options="[{
              'text': $t('purchase.purchases'),
              'value': 'purchases'
            }, {
              'text': $t('purchase.noPurchases'),
              'value': 'no-purchases'
            }]">
          </d-form-select>
        </d-col>

        <d-col cols="12" md="3" lg="3">
          <label>
            {{ $t('report.dateRange') }}:
          </label>

          <date-range-picker
            ref="purchase-report-date-picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="w-100"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="purchaseReport.date_range"
            :linkedCalendars="false">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
            </template>
          </date-range-picker>
        </d-col>

        <d-col cols="12" md="3" lg="3">
          <label>
            {{ $t('product.products') }}:
          </label>

          <div class="form-control is-select">
            <v-select
              :options="productCodesOptions"
              v-model="purchaseReport.product_codes"
              ref="product-codes-filter"
              :placeholder="$t('product.productsCodes')"
              :searchable="true"
              :multiple="true"
              label="text"
              :filter="filteredProductCodes">
              <template slot="no-options">
                <template v-if="$refs['product-codes-filter'] && $refs['product-codes-filter'].search.length">
                  {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                </template>
                <template v-else>
                  {{ $t('product.enterProductCodeForSearch') }}
                </template>
              </template>
            </v-select>
          </div>
        </d-col>

        <d-col cols="12" md="2" lg="2">
          <label>
            {{ $t('settings.country.country') }}:
          </label>

          <div class="form-control is-select">
            <v-select
              :options="countriesOptions"
              v-model="purchaseReport.country"
              :placeholder="$t('customer.customerCountryQuestionMark')"
              :searchable="false"
              label="text">
            </v-select>
          </div>
        </d-col>

        <d-col cols="12" md="2" lg="2">
          <label class="w-100"></label>

          <d-button
            theme="primary"
            class="w-100 mt-2"
            @click.prevent="downloadPurchaseReport">
            <template v-if="!downloadingPurchaseReport">
              {{ $t('report.generateReport') }}
            </template>
            <template v-else>
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </template>
          </d-button>
        </d-col>
      </d-row>
    </d-card>
  </d-container>
</template>

<script>
import FormUtils from './../utils/FormUtils.js';
import SelectCustomer from './../components/common/SelectCustomer.vue';
import SelectUser from './../components/common/SelectUser.vue';
import Vue from 'vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'reports',
  components: {
    'date-range-picker': DateRangePicker,
    'select-customer': SelectCustomer,
    'select-user': SelectUser
  },
  computed: {
    allDataLoaded () {
      return this.loadedCountries && this.loadedProducts;
    },
    countriesOptions () {
      return this.countries.map(country => ({
        text: country.name,
        value: country.id
      }));
    },
    productCodesOptions () {
      let options = [];

      if (this.$refs['product-codes-filter'] && this.$refs['product-codes-filter'].search.length < 1) {
        return options;
      }

      for (let i = 0; i < this.products.length; i++) {
        let option = {
          text: this.products[i].code,
          value: this.products[i].id
        };

        options.push(option);
      }

      return options;
    }
  },
  data() {
    let previousWeek = new Date();
    previousWeek.setDate(previousWeek.getDate() - 6);

    return {
      dailyReport: {
        customer_id: null,
        user_id: null,
        date_range: {
          startDate: previousWeek,
          endDate: new Date()
        }
      },
      purchaseReport: {
        type: 'purchases',
        country: null,
        product_codes: null,
        date_range: {
          startDate: previousWeek,
          endDate: new Date()
        }
      },
      downloadingDailyReport: false,
      downloadingPurchaseReport: false,
      products: [],
      countries: [],
      loadedProducts: false,
      loadedCountries: false,
      loadError: false
    };
  },
  mounted () {
    this.$bus.$on('ui-select-user-updated', this.updateDailyReportUser);
    this.$bus.$on('ui-select-customer-updated', this.updateDailyReportCustomer);
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });
    },
    downloadDailyReport () {
      this.downloadingDailyReport = true;
      let params = {};

      if (this.dailyReport.customer_id !== null) {
        params.customer_id = parseInt(this.dailyReport.customer_id, 10);
      }

      if (this.dailyReport.user_id !== null) {
        params.user_id = parseInt(this.dailyReport.user_id, 10);
      }

      if (this.dailyReport.date_range.startDate !== null) {
        params.date = this.prepareDateString(this.dailyReport.date_range.startDate);
      }

      let filename = ['dzienny-raport'];

      if (this.dailyReport.date_range.startDate !== null) {
        filename.push(this.prepareDateString(this.dailyReport.date_range.startDate));
      }

      filename = filename.join('-') + '.xlsx';

      this.$http.post('/reports/orders/ordered-products', {
        where: params
      }, {
        responseType: 'blob',
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingDailyReport = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingDailyReport = false;
      });
    },
    downloadPurchaseReport () {
      this.downloadingPurchaseReport = true;

      let params = {
        type: this.purchaseReport.type
      };

      if (this.purchaseReport.country !== null) {
        params.country_id = parseInt(this.purchaseReport.country.value, 10);
      }

      if (this.purchaseReport.date_range.startDate !== null) {
        params.date_from = this.prepareDateString(this.purchaseReport.date_range.startDate);
      }

      if (this.purchaseReport.date_range.endDate !== null) {
        params.date_to = this.prepareDateString(this.purchaseReport.date_range.endDate);
      }

      if (this.purchaseReport.product_codes !== null) {
        params.products = this.purchaseReport.product_codes.map(product => product.value);
      }

      let filename = ['raport-zakupow'];

      if (this.purchaseReport.date_range.startDate !== null) {
        filename.push(this.prepareDateString(this.purchaseReport.date_range.startDate));
      }

      if (this.purchaseReport.date_range.endDate !== null) {
        filename.push(this.prepareDateString(this.purchaseReport.date_range.endDate));
      }

      filename = filename.join('-') + '.xlsx';

      this.$http.post('/reports/customers/purchases', {
        where: params
      }, {
        responseType: 'blob',
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPurchaseReport = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingPurchaseReport = false;
      });
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    updateDailyReportUser (newValue) {
      this.dailyReport.user_id = newValue;
    },
    updateDailyReportCustomer (newValue) {
      this.dailyReport.customer_id = newValue;
    },
    filteredProductCodes (options, search) {
      return options.filter(option => this.filterByProductCodes(option, option.id, search)).slice(0, 50);
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.chooseDate');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-user-updated', this.updateDailyReportUser);
    this.$bus.$off('ui-select-customer-updated', this.updateDailyReportCustomer);
  }
};
</script>
